import InformationSection from './sections/home/InformationSection'
import ProfileSection from './sections/home/ProfileSection'
import ServiceSection from './sections/home/ServiceSection'
import { useGetProject } from '../hooks/useGetProject'
import { useHasRentalItems } from '../hooks/useHasRentalItems'
import NotFound from './NotFound'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loading } from './Loading'
import LoginModal from './modals/LoginModal'
import ProductSection from './sections/home/ProductSection'
import Footer from '../components/footer'
import HeroSection from './sections/home/HeroSection'
import { useLocation, useNavigate } from 'react-router'
import { CalendarCheck, ChevronRight, PackageOpen, type LucideIcon } from 'lucide-react'
import { scrollToElement } from '../utils/scroll'
import { getReadablePrimaryButtonStyle, resolveThemeColor } from '../utils/buttonColor'

function Home() {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
  const { t } = useTranslation('home')

  const { project, isLoading } = useGetProject()
  const themeColor = resolveThemeColor(project?.themeColor)
  const buttonColor = resolveThemeColor(project?.buttonColor)
  // Looked up once here and handed down, so the hero and the section below it agree without
  // asking the server twice.
  const { hasItems: canRent } = useHasRentalItems(project)
  const { hash } = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (isLoading || !project || !hash) return
    const id = hash.replace(/^#/, '')
    if (!id) return
    // Defer to allow sections to render before scrolling.
    const timer = setTimeout(() => scrollToElement(id), 50)
    return () => clearTimeout(timer)
  }, [hash, isLoading, project])

  if (isLoading) return <Loading />

  if (!project) return <NotFound />

  return (
    <div className="App">
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      <HeroSection
        project={project}
        openLoginModal={() => setIsLoginModalOpen(true)}
        canRent={canRent}
      />
      <InformationSection project={project} />
      <ProfileSection project={project} />
      <ProductSection project={project} />
      <ServiceSection />
      <section
        id="book"
        className="px-5 py-14 sm:py-16"
        style={{
          background: `linear-gradient(135deg, ${themeColor}0d 0%, transparent 60%)`,
        }}
      >
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl">
            {t('readyToBook')}
          </h2>
          <p className="mt-3 text-sm text-gray-600 sm:text-base">
            {canRent ? t('readyToBookSubtitle') : t('readyToBookSubtitleBookingOnly')}
          </p>
          {canRent ? (
            /* Two destinations of equal standing. Presented as cards rather than a filled
               button beside an outlined one, which reads as a confirm/cancel pair and makes
               the second option look like the one you are meant to decline. */
            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              <ActionCard
                icon={CalendarCheck}
                title={t('bookNow')}
                description={t('bookNowSubtitle')}
                accent={buttonColor}
                onClick={() => navigate('/booking')}
              />
              <ActionCard
                icon={PackageOpen}
                title={t('rentAnItem')}
                description={t('rentAnItemSubtitle')}
                accent={buttonColor}
                onClick={() => navigate('/rent')}
              />
            </div>
          ) : (
            /* One action needs no card to distinguish it from anything. */
            <button
              type="button"
              onClick={() => navigate('/booking')}
              className="mt-7 w-full rounded-xl px-8 py-3.5 text-sm font-semibold shadow-md transition hover:brightness-105 active:brightness-95 sm:w-auto"
              style={getReadablePrimaryButtonStyle(buttonColor)}
            >
              {t('bookNow')}
            </button>
          )}
        </div>
      </section>
      <Footer />
    </div>
  )
}

/**
 * Both cards carry the project's button colour, and both carry the same one: the pair reads
 * as two ways in rather than as an action and its refusal, which is what a filled button
 * beside an outlined one looks like.
 */
function ActionCard({
  icon: Icon,
  title,
  description,
  accent,
  onClick,
}: {
  icon: LucideIcon
  title: string
  description: string
  accent: string
  onClick: () => void
}) {
  // Picks a legible foreground, so a pale brand colour does not leave white text on white.
  const { backgroundColor, color } = getReadablePrimaryButtonStyle(accent)
  // The icon tile is a wash of the text colour, which keeps it visible on a pale brand
  // colour where a white tile disappears into the card.
  const isDarkText = color !== '#ffffff'

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-4 rounded-2xl p-5 text-left shadow-md transition hover:-translate-y-0.5 hover:brightness-105 active:translate-y-0 active:brightness-95"
      style={{ backgroundColor, color }}
    >
      <span
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
        style={{ backgroundColor: isDarkText ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.2)' }}
        aria-hidden
      >
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold">{title}</span>
        <span className="mt-0.5 block text-xs opacity-80">{description}</span>
      </span>
      <ChevronRight
        className="h-5 w-5 shrink-0 opacity-70 transition group-hover:translate-x-0.5 group-hover:opacity-100"
        aria-hidden
      />
    </button>
  )
}

export default Home
